import _ from "lodash";
import Promise from "bluebird";
import dateFormat from "dateformat";
import BaseApiModel from "scripts/models/baseApiModel";
import HybridModelHoc from "scripts/hoc/hybridModelHoc";
import Util from "scripts/utils/util";
import inject from "scripts/ioc/inject";

import fallBackImage from "images/fallbacks/fallback_469_625.jpg";

class ContentModel extends HybridModelHoc(BaseApiModel) {
  constructor(
    attrs,
    options,
    bookshelfService = inject("bookshelfService"),
    securityService = inject("securityService"),
  ) {
    super(attrs, options);
    this.bookshelfService = bookshelfService;
    this.securityService = securityService;
  }

  urlRoot() {
    return `${this.apiUrlRoot}/content/`;
  }

  parse(response) {
    if ("contributors" in response) {
      // Overwrite contributors with ordered contributors having formatted name and role
      response.contributors = Util.getFormattedContributors(response.contributors, true);

      // Add comma separated contributors
      response.contributorList = response.contributors
        .map(contributor => `${contributor.name} (${contributor.role})`)
        .join(", ");
    }

    // Strip language for "No linguistic content"
    if (response.languageIso639_2 === "zxx") {
      response.language = "";
    }

    // Format pub date
    if (response.publicationDate) {
      let format;

      if (response.publicationDateCirca) {
        format = "UTC:yyyy";
      } else if (_.includes(["ARTICLE", "OTHER_DOCUMENT"], response.type)) {
        format = "UTC:mm/dd/yyyy";
      } else {
        if (this.isJanuaryFirst(response.publicationDate)) {
          format = "UTC:yyyy";
        } else {
          format = "UTC:mm/dd/yyyy";
        }
      }

      response.publicationDateIso = response.publicationDate;
      response.publicationDate = dateFormat(response.publicationDate, format);
    }

    // Add thumbnail fallback image
    response.thumbnailFallbackUrl = fallBackImage;

    // Make it easy to check if EPUB
    response.isEpub = response.mimeType && response.mimeType.indexOf("epub") !== -1;

    // filter badge types
    response.whiteMediaBadges = _.filter(response.mediaBadges, {
      type: "white",
    });
    response.blackMediaBadges = _.filter(response.mediaBadges, {
      type: "black",
    });

    response.awardMediaBadges = this.getAwards(response.mediaBadges);

    if (response.productIdentifiers !== undefined) {
      // ContentDetailsSubPageView parses DOI as HTML; grab DOI from there for now
      response.productIdentifiers = _.reject(response.productIdentifiers, identifier =>
        _.includes(["DOI"], identifier.type),
      );
    }

    // create a unique-ish label for aria-label tags
    response.ariaLabel = response.subtitle ? response.title + " - " + response.subtitle : response.title;

    response.typeDisplay = response.type ? response.type.replace("_", " ") : "";

    return response;
  }

  isBookshelfEligible() {
    return _.includes(["BOOK", "ARTICLE", "OTHER_DOCUMENT", "IMAGE", "CHAPTER"], this.get("type"));
  }

  fetchHybridData() {
    if (this.isBookshelfEligible() && this.bookshelfService.isAvailable()) {
      return this.getBookshelfData().then(bookshelfData => this.addImageUrls(bookshelfData));
    }
  }

  getBookshelfData() {
    const organizationId = this.securityService.getUser().getActiveOrganizationId();
    return this.bookshelfService.getBookshelfData(this.get("id"), organizationId);
  }

  addImageUrls(bookshelfData) {
    if (bookshelfData.onBookshelf) {
      const organizationId = this.securityService.getUser().getActiveOrganizationId();
      return Promise.all([
        this.bookshelfService
          .getLocalContentFileUrlForRemoteUrl(this.get("thumbnailUrl"), this.get("id"), organizationId, "data")
          .catch(() => {
            //console.log('Failed to find thumbnail url on filesystem: ' + this.get('thumbnailUrl'));
            return this.get("thumbnailUrl");
          }),
        this.bookshelfService
          .getLocalContentFileUrlForRemoteUrl(this.get("imageUrl"), this.get("id"), organizationId, "data")
          .catch(() => {
            //console.log('Failed to find image url on filesystem: ' + this.get('imageUrl'));
            return this.get("imageUrl");
          }),
      ]).then(([thumbnailUrl, imageUrl]) => {
        return {
          onBookshelf: bookshelfData.onBookshelf,
          pdfOnBookshelf: bookshelfData.pdfOnBookshelf,
          downloadComplete: bookshelfData.downloadComplete,
          totalSize: bookshelfData.totalSize,
          thumbnailUrl: thumbnailUrl,
          imageUrl: imageUrl,
        };
      });
    } else {
      return {
        onBookshelf: bookshelfData.onBookshelf,
        pdfOnBookshelf: bookshelfData.pdfOnBookshelf,
        downloadComplete: bookshelfData.downloadComplete,
        totalSize: bookshelfData.totalSize,
      };
    }
  }

  parseHybridData(data) {
    this.set(data);
  }

  isRtl() {
    return this.get("textDirectionality") === "RTL";
  }

  // For now, just grab first 4 awards
  getAwards(badges) {
    return _.slice(_.filter(badges, { type: "award" }), 0, 4);
  }

  isJanuaryFirst(isoDate) {
    // yyyy-mm-dd
    const withoutTimeZone = _.chain(isoDate).split("T").head().value();
    // remove "yyyy-"
    return withoutTimeZone.substring(5) === "01-01";
  }
}

export default ContentModel;
